import { useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";

import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useCustomerViewModel } from "../../../viewmodels/operation/customer/useCustomerViewModel";

import { useWeightTestViewModel } from "../../../viewmodels/operation/weighttest/useWeightTestViewModel";

const CreateWeightTest = () => {
  const navigate = useNavigate();

  const { createWeightTest } = useWeightTestViewModel();

  const { customers, setSearch } = useCustomerViewModel();

  // ================= CUSTOMER =================
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const customerDropdownRef = useRef<any>(null);

  const [_selectedCustomer, setSelectedCustomer] = useState<any>(null);

  // ================= FORM =================
  const [form, setForm] = useState<any>({
    customerName: "",
    customerAddress: "",

    signature: "",
    signedBy: "",
    designation: "",

    date: "",
    dueDate: "",

    isPrinted: 0,
  });

  // ================= LINE ITEMS =================
  const [items, setItems] = useState<any[]>([
    {
      capacity: "",
      capacityUnit: "",
      actualReading: "",
      actualReadingUnit: "",
      weightBox: "",
    },
  ]);

  // ================= OUTSIDE CLICK =================
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ================= SELECT CUSTOMER =================
  const handleSelectCustomer = (customer: any) => {
    setSelectedCustomer(customer);

    const firstAddress = customer?.addresses?.[0];

    const fullAddress = [
      firstAddress?.addressLine1,
      firstAddress?.addressLine2,
      firstAddress?.city,
      firstAddress?.state,
      firstAddress?.postalCode,
    ]
      .filter((x) => x && x.trim() !== "")
      .join(", ");

    setForm({
      ...form,

      customerName: customer.name || "",

      customerAddress: fullAddress,
    });

    setShowCustomerDropdown(false);
  };

  // ================= ADD ROW =================
  const addRow = () => {
    setItems([
      ...items,

      {
        capacity: "",
        capacityUnit: "",

        actualReading: "",
        actualReadingUnit: "",

        weightBox: "",
      },
    ]);
  };

  // ================= REMOVE ROW =================
  const removeRow = (index: number) => {
    const updated = [...items];

    updated.splice(index, 1);

    setItems(updated);
  };

  // ================= SAVE =================
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!form.customerName) {
      toast.error("Customer name required");

      return;
    }

    const payload = {
      customerName: form.customerName,

      customerAddress: form.customerAddress,

      signature: form.signature,

      signedBy: form.signedBy,

      designation: form.designation,

      date: form.date ? new Date(form.date).toISOString() : null,

      dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,

      isPrinted: Number(form.isPrinted),

      weightTestLineItems: items.map((x) => ({
        capacity: Number(x.capacity),

        capacityUnit: x.capacityUnit,

        actualReading: Number(x.actualReading),

        actualReadingUnit: x.actualReadingUnit,

        weightBox: x.weightBox,
      })),
    };

    const success = await createWeightTest(payload);

    if (success) {
      navigate("/weight-test-reports");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Create Weight Test</h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* ================= CUSTOMER ================= */}
          <div className="bg-white border rounded-3xl p-6 space-y-6">
            <div className="border-b pb-4">
              <h2 className="text-2xl font-bold">Customer Details</h2>

              <p className="text-gray-500 text-sm mt-1">
                Select existing customer or manually enter details
              </p>
            </div>

            {/* CUSTOMER NAME */}
            <div>
              <label className="block mb-2 font-medium">Customer Name</label>

              <div className="relative" ref={customerDropdownRef}>
                <input
                  required
                  value={form.customerName}
                  onFocus={() => setShowCustomerDropdown(true)}
                  onChange={(e) => {
                    const value = e.target.value;

                    setForm({
                      ...form,

                      customerName: value,
                    });

                    setSearch(value);

                    setShowCustomerDropdown(true);
                  }}
                  placeholder="Search customer"
                  className="w-full border rounded-xl px-4 py-3"
                />

                {/* DROPDOWN */}
                {showCustomerDropdown && form.customerName.trim() !== "" && (
                  <div className="absolute z-50 bg-white border rounded-xl shadow-lg w-full mt-2 max-h-72 overflow-auto">
                    {/* MANUAL ENTRY */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);

                        setShowCustomerDropdown(false);
                      }}
                      className="w-full text-left px-4 py-3 bg-primary text-white font-semibold hover:opacity-90"
                    >
                      Manual Entry
                    </button>

                    {/* CUSTOMERS */}
                    {customers.map((x: any) => (
                      <button
                        key={x.id}
                        type="button"
                        onClick={() => handleSelectCustomer(x)}
                        className="w-full text-left px-4 py-3 border-b hover:bg-slate-100"
                      >
                        <div className="font-semibold">{x.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ADDRESS */}
            <div>
              <label className="block mb-2 font-medium">Customer Address</label>

              <textarea
                rows={3}
                value={form.customerAddress}
                onChange={(e) =>
                  setForm({
                    ...form,

                    customerAddress: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3 resize-none"
              />
            </div>
          </div>

          {/* ================= DETAILS ================= */}
          <div className="bg-white border rounded-3xl p-6">
            <div className="border-b pb-4 mb-6">
              <h2 className="text-2xl font-bold">Weight Test Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block mb-2 font-medium">Date</label>

                <input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      date: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">Due Date</label>

                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      dueDate: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>
            </div>
          </div>

          {/* ================= TABLE ================= */}
          <div className="bg-white border rounded-3xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Weight Test Items</h2>

              <button
                type="button"
                onClick={addRow}
                className="px-5 py-3 bg-primary text-white rounded-2xl"
              >
                + Add Row
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="border p-3">Capacity</th>

                    <th className="border p-3">Capacity Unit</th>

                    <th className="border p-3">S/No.</th>

                    <th className="border p-3">Actual Reading</th>

                    <th className="border p-3">Actual Reading Unit</th>

                    <th className="border p-3">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="border p-2">
                        <input
                          value={item.capacity}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].capacity = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </td>

                      <td className="border p-2">
                        <select
                          value={item.capacityUnit}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].capacityUnit = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="">Select</option>

                          <option value="t">t</option>

                          <option value="kg">kg</option>

                          <option value="g">g</option>

                          <option value="mg">mg</option>

                          <option value="lb">lb</option>

                          <option value="oz">oz</option>
                        </select>
                      </td>

                      <td className="border p-2">
                        <input
                          value={item.weightBox}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].weightBox = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </td>

                      <td className="border p-2">
                        <input
                          value={item.actualReading}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].actualReading = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </td>

                      <td className="border p-2">
                        <select
                          value={item.actualReadingUnit}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].actualReadingUnit = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="">Select</option>

                          <option value="t">t</option>

                          <option value="kg">kg</option>

                          <option value="g">g</option>

                          <option value="mg">mg</option>

                          <option value="lb">lb</option>

                          <option value="oz">oz</option>
                        </select>
                      </td>

                      <td className="border p-2">
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded-xl"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= FOOTER ================= */}
          <div className="bg-white border rounded-3xl p-6">
            <div className="border-b pb-4 mb-6">
              <h2 className="text-2xl font-bold">Signature Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* <div>

                  <label className="block mb-2 font-medium">
                    Signature
                  </label>

                  <input
                    value={
                      form.signature
                    }
                    onChange={(e) =>
                      setForm({

                        ...form,

                        signature:
                          e.target.value,

                      })
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />

                </div> */}

              <div>
                <label className="block mb-2 font-medium">Signed By</label>

                <input
                  value={form.signedBy}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      signedBy: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">Designation</label>

                <input
                  value={form.designation}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      designation: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>
            </div>
          </div>

          {/* BUTTON */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-primary text-white px-8 py-4 rounded-2xl font-semibold"
            >
              Create Weight Test
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CreateWeightTest;
